import { type SelectOption } from '@aus-platform/design-system';
import { type FormatOptionLabelMeta, type StylesConfig } from 'react-select';
import {
  type DashboardDataCategoryEntry,
  type DashboardVolumeType,
  type HeapClusterResponseDataTypeObj,
} from 'shared/api';
import { type DashboardCategoryConfig } from '../heap-list';

export enum DashboardModalStep {
  Configuration = 'configuration',
  Attachments = 'attachments',
}

export enum DashboardModalMode {
  Add = 'add',
  Edit = 'edit',
}

export type DashboardAttachment = {
  id?: string;
  file?: File;
  fileName: string;
  fileSize?: number;
};

export type LeadDistanceEntry = {
  id?: string;
  clientId: string;
  category3: SelectOption<DashboardDataCategoryEntry> | null;
  category4: SelectOption<DashboardDataCategoryEntry> | null;
  dumpVolume: number | null;
  bulkingFactor: number | null;
  pitDumpLeadDistanceKm: number | null;
  intraPitLeadDistanceKm: number | null;
  intraDumpLeadDistanceKm: number | null;
};

export type LeadDistanceField = keyof Omit<
  LeadDistanceEntry,
  'id' | 'clientId'
>;

export type LeadDistanceErrors = Partial<Record<LeadDistanceField, string>>;

export type LeadDistanceSelectField = Extract<
  LeadDistanceField,
  'category3' | 'category4'
>;

export type LeadDistanceNumberField = Exclude<
  LeadDistanceField,
  LeadDistanceSelectField
>;

export type DashboardModalProps = {
  onClose: () => void;
  selectedDashboardCluster: HeapClusterResponseDataTypeObj;
  dashboardCategoryConfig: DashboardCategoryConfig[];
  contentClassName?: string;
  refetchHeapList: VoidFunction;
};

export type ClusterDetailsAccordionProps = {
  selectedDashboardCluster: HeapClusterResponseDataTypeObj;
  children?: React.ReactNode;
};

export type ClusterVolumeMeasurementsProps = {
  selectedDashboardCluster: HeapClusterResponseDataTypeObj;
  volumeType: DashboardVolumeType;
};

export type ClusterCumulativeMeasurements = Pick<
  HeapClusterResponseDataTypeObj,
  | 'cumulativeCutVolume'
  | 'cumulativeFillVolume'
  | 'cumulativeNetVolume'
  | 'cumulativeCutWeight'
  | 'cumulativeFillWeight'
  | 'cumulativeNetWeight'
>;

export type ClusterVolumeAndWeight = {
  volume: string | null;
  weight: string | null;
};

export type ClusterDetailItem = {
  label: string;
  value?: string;
  fullWidth?: boolean;
  customRenderer?: () => React.ReactNode;
};

export type DashboardBaseFields = {
  category1: SelectOption<DashboardDataCategoryEntry> | null;
  category2: SelectOption<DashboardDataCategoryEntry> | null;
  accountingMonth: Date | null;
  volumeType: SelectOption<DashboardVolumeType>;
};

export type DashboardConfigurationType = DashboardBaseFields & {
  leadDistances: LeadDistanceEntry[];
  deletedLeadDistanceIds: string[];
  includeLeadDistanceInfo: boolean;
};

export type SupportedFormat = {
  category: string;
  formats: string;
};

export type DashboardConfigurationProps = {
  selectedDashboardCluster: HeapClusterResponseDataTypeObj;
  dashboardFormValues: DashboardConfigurationType;
  setDashboardFormValues: React.Dispatch<
    React.SetStateAction<DashboardConfigurationType>
  >;
  setIsNextDisabled: (isNextDisabled: boolean) => void;
  isLeadDistanceEnabled: boolean;
  setIsLeadDistanceEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  dashboardCategoryConfig: DashboardCategoryConfig[];
};

export type LeadDistanceSelectStyles = StylesConfig<
  SelectOption<DashboardDataCategoryEntry>,
  boolean
>;

export type LeadDistanceOptionFormatter = (
  option: SelectOption<DashboardDataCategoryEntry>,
  meta: FormatOptionLabelMeta<SelectOption<DashboardDataCategoryEntry>>,
) => React.ReactNode;

export type LeadDistanceCardProps = {
  entry: LeadDistanceEntry;
  errors: LeadDistanceErrors;
  category3Config?: DashboardCategoryConfig;
  category4Config?: DashboardCategoryConfig;
  selectStyles: LeadDistanceSelectStyles;
  sortOptionsByActive: (
    options?: SelectOption<DashboardDataCategoryEntry>[],
  ) => SelectOption<DashboardDataCategoryEntry>[];
  formatOptionLabel: LeadDistanceOptionFormatter;
  onSelectChange: (
    clientId: string,
    field: LeadDistanceSelectField,
    option: SelectOption<DashboardDataCategoryEntry>,
  ) => void;
  onNumberChange: (
    clientId: string,
    field: LeadDistanceNumberField,
    value: number | null,
  ) => void;
  onFieldBlur: (clientId: string, field: LeadDistanceField) => void;
  onFieldFocus: (clientId: string, field: LeadDistanceField) => void;
};

export type Attachment = {
  id: string;
  file: File;
  fileUploadId?: string;
  isExisting?: boolean;
};

export type DashboardAttachmentsProps = {
  handleBack: () => void;
  attachments: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  currentStep: DashboardModalStep;
  shouldCancelUploads: boolean;
  setIsUploadingFiles: React.Dispatch<React.SetStateAction<boolean>>;
  isDropZoneDisabled: boolean;
};

export type AttachmentFileCardProps = {
  selectedFile: Attachment;
  onRemoveFile: (id: string, fileUploadId?: string | null) => void;
  onUploadStatusChange: (id: string, isUploading: boolean) => void;
  shouldCancelUpload: boolean;
};
